import { eventNames, useCoreEvents } from '@carvana/core-events';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { DispatcherEvent } from '@carvana/merch-native';
import React, { useCallback, useContext } from 'react';
import { ToastActions } from '~/components/common/FavoritesToastContent/types';
import useDisplayFavoritesToast from '~/components/common/FavoritesToastContent/useDisplayFavoritesToast';
import SavedSearchesContext from '~/context/common/SavedSearchesContext';
import {
  useIsLoggedIn,
  useUserEmail,
  useUserId,
  useUserLocation
} from '~/context/common/UserContext';
import ModalContext from '~/context/home/ModalContext';
import RecentSearchesContext from '~/context/home/RecentSearchesContext';
import { RecentSearch } from '~/contracts/home';
import { useOpenAccountModal } from '~/hooks/common/accountModal/useAccountModal';
import sendMessageToNative from '~/native/home/dispatchers/sendMessageToNative';
import { deleteSavedSearchRCB } from '~/services/common/savedSearch';
import { BrowserCookies } from '~/utilities';
import decodeCvnaId from '~/utilities/cvnaId';

type OwnProps = {
  search: RecentSearch;
};

const updateRecentOnRemove = (
  recentReaches: RecentSearch[],
  targetCvnaId: string
) =>
  recentReaches.map(recentSearch => {
    if (recentSearch.cvnaId !== targetCvnaId) return recentSearch;

    return {
      ...recentSearch,
      savedSearch: undefined
    };
  });

export default function useToggleSavedSearches({ search }: OwnProps) {
  const { savedSearch, display, cvnaId } = search;
  const alertName = savedSearch?.name || display.name;
  const { trackEvent } = useContext(AnalyticsContext);
  const { savedSearches } = useContext(SavedSearchesContext);
  const {
    recentSearches: { data: recentSearches, setData: setRecentSearches }
  } = useContext(RecentSearchesContext);
  const { setSaveSearchModalOpen } = useContext(ModalContext);
  const displayFavoritesToast = useDisplayFavoritesToast();
  const isLoggedIn = useIsLoggedIn();

  const successHandler = useCallback(() => {
    const payload = decodeCvnaId(cvnaId)?.filters ?? {};

    setSaveSearchModalOpen(true, payload);
  }, [cvnaId, setSaveSearchModalOpen]);

  const openAccountModal = useOpenAccountModal();

  const openModal = useCallback(() => {
    if (isLoggedIn) successHandler();

    openAccountModal({
      callerIdentifier: 'SearchAlert',
      nativeOptions: {
        eventDispatch: DispatcherEvent.AddSavedSearch,
        eventPayload: { alertName, cvnaid: cvnaId }
      },
      onSuccess: successHandler
    });
  }, [alertName, cvnaId, isLoggedIn, openAccountModal, successHandler]);

  const email = useUserEmail();
  const userId = useUserId();
  const { zip } = useUserLocation();

  const { emit } = useCoreEvents();

  return useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (savedSearch?.savedSearchId) {
        deleteSavedSearchRCB({
          browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
          email,
          savedSearchId: savedSearch.savedSearchId,
          userId,
          zip5: zip
        }).then(res => {
          savedSearches.setData(res.data);
          setRecentSearches(updateRecentOnRemove(recentSearches, cvnaId));
          sendMessageToNative(DispatcherEvent.SavedSearchDeleted, {});
          emit(eventNames.favorites.mfe.refresh, {
            searches: res.data,
            source: 'app'
          });
          displayFavoritesToast(ToastActions.remove);
        });

        trackEvent('Home feed card', {
          action: 'clicked',
          content: display,
          description: 'Recent search heart cta',
          status: 'removed'
        });

        return;
      }

      openModal();

      trackEvent('Home feed card', {
        action: 'clicked',
        content: display,
        description: 'Recent search heart cta',
        status: 'opened'
      });
    },
    [
      cvnaId,
      display,
      displayFavoritesToast,
      email,
      emit,
      openModal,
      recentSearches,
      savedSearch?.savedSearchId,
      savedSearches,
      setRecentSearches,
      trackEvent,
      userId,
      zip
    ]
  );
}
